import type { OnboardingDataEntry } from '@onefootprint/request-types/dashboard';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArtifactViewerNavList from './components/artifacts-nav-list';
import DataArtifactsView from './components/data-artifact-view';
import FileArtifactView from './components/file-artifact-view';
import TabBar from './components/tab-bar';
export type NormalizedArtifact = OnboardingDataEntry & {
  artifactType: 'data' | 'file';
};

// Referred to as "artifacts" in the product
// Named OnboardingDataEntryViewer here to avoid confusion with the Artifact and ArtifactViewer components for Percy
type OnboardingDataEntryViewerProps = {
  artifacts: NormalizedArtifact[];
  height?: number;
};
export const getArtifactId = (artifact: NormalizedArtifact) => `${artifact.artifactType}:${artifact.key}:${artifact.modifiedAt}`;
const OnboardingDataEntryViewer = ({
  artifacts,
  height = 600
}: OnboardingDataEntryViewerProps) => {
  const {
    t
  } = useTranslation('onboarding-data-entry-viewer');
  const [activeTab, setActiveTab] = useState<NormalizedArtifact | null>(null);
  const [openTabs, setOpenTabs] = useState<NormalizedArtifact[]>([]);
  const fileArtifacts = artifacts.filter(a => a.artifactType === 'file').sort((a, b) => a.key.localeCompare(b.key));
  const dataArtifacts = artifacts.filter(a => a.artifactType === 'data').sort((a, b) => a.key.localeCompare(b.key));
  const sortedArtifacts = [...fileArtifacts, ...dataArtifacts];
  const openArtifact = (artifact: NormalizedArtifact) => {
    setOpenTabs(prev => {
      const alreadyOpen = prev.some(t => getArtifactId(t) === getArtifactId(artifact));
      return alreadyOpen ? prev : [...prev, artifact];
    });
    setActiveTab(artifact);
  };
  const closeTab = (artifact: NormalizedArtifact) => {
    setOpenTabs(prev => {
      const filtered = prev.filter(t => getArtifactId(t) !== getArtifactId(artifact));
      if (activeTab && getArtifactId(activeTab) === getArtifactId(artifact)) {
        const closedIndex = prev.findIndex(t => getArtifactId(t) === getArtifactId(artifact));
        const nextTab = filtered[Math.min(closedIndex, filtered.length - 1)] ?? null;
        setActiveTab(nextTab);
      }
      return filtered;
    });
  };
  useEffect(() => {
    if (openTabs.length === 0 && activeTab === null && sortedArtifacts.length > 0) {
      openArtifact(sortedArtifacts[0]);
    }
    // Only auto-open on initial mount, not when user closes all tabs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedArtifacts]);
  return <div className="flex flex-col w-full border border-tertiary rounded-md overflow-hidden md:flex-row" style={{
    height: sortedArtifacts.length > 0 ? height : 'auto'
  }}>
      {sortedArtifacts.length > 0 ? <>
          <ArtifactViewerNavList fileArtifacts={fileArtifacts} dataArtifacts={dataArtifacts} onSelectArtifact={openArtifact} selectedArtifact={activeTab} />
          <div className="flex-1 flex flex-col min-w-0">
            <TabBar tabs={openTabs} activeTab={activeTab} onSelectTab={setActiveTab} onCloseTab={closeTab} />
            <div className="flex-1 overflow-auto">
              {activeTab?.artifactType === 'data' && <DataArtifactsView artifact={activeTab} />}
              {activeTab?.artifactType === 'file' && <FileArtifactView artifact={activeTab} />}
            </div>
          </div>
        </> : <div className="flex items-center justify-center w-full h-full text-tertiary text-body-2 p-4">
          {t($ => $.noArtifacts)}
        </div>}
    </div>;
};
export default OnboardingDataEntryViewer;